// Lets the user point at the element(s) where something goes wrong. A transparent layer
// inside our shadow root catches all pointer input, so the host page never receives clicks
// while picking; the element underneath is found with elementsFromPoint().
import { h, icon } from './dom.ts';
import { T } from './texts.ts';

export const MAX_PICKS = 5;

export interface PickerOptions {
  /** Container inside the shadow root. */
  mount: HTMLElement;
  host: Element;
  initial: Element[];
  banner: string;
  announce: (message: string) => void;
}

function box(className: string): HTMLDivElement {
  return h('div', { class: className, attrs: { 'aria-hidden': 'true' } });
}

function place(el: HTMLElement, rect: DOMRect): void {
  el.style.transform = `translate(${rect.left - 3}px, ${rect.top - 3}px)`;
  el.style.width = `${rect.width + 6}px`;
  el.style.height = `${rect.height + 6}px`;
}

/** Resolves with the picked elements, or null if the user cancelled. */
export function pickElements({ mount, host, initial, banner, announce }: PickerOptions): Promise<Element[] | null> {
  return new Promise((resolve) => {
    const picked: Element[] = [...initial];
    const markers: HTMLDivElement[] = [];
    let hovered: Element | null = null;
    let frame = 0;

    const hover = box('gsb-pick-hover');
    const layer = h('div', { class: 'gsb-pick-layer', attrs: { 'data-gsb-ignore': '' } });
    const count = h('span', { class: 'gsb-pick-count', attrs: { 'aria-live': 'polite' } });
    const done = h('button', { class: 'gsb-btn gsb-btn-primary gsb-btn-small', attrs: { type: 'button' } }, [icon('check', 18), T.pickDone]);
    const cancel = h('button', { class: 'gsb-btn gsb-btn-ghost-light gsb-btn-small', attrs: { type: 'button' }, text: T.pickCancel });
    // The bar stays where it is unless the user moves it: moving it automatically with the
    // pointer made the buttons unreachable (it fled whenever the pointer came close).
    const move = h('button', { class: 'gsb-icon-btn gsb-pick-move', attrs: { type: 'button' } });
    const bar = h('div', { class: 'gsb-pick-bar', attrs: { role: 'dialog', 'aria-label': T.pickStart } }, [
      h('span', { class: 'gsb-pick-icon' }, [icon('target', 22)]),
      h('span', { class: 'gsb-pick-text' }, [h('span', { text: banner }), count]),
      h('span', { class: 'gsb-pick-actions' }, [move, cancel, done]),
    ]);
    function renderMove(): void {
      const atBottom = bar.classList.contains('gsb-pick-bar-bottom');
      const label = atBottom ? T.pickBarUp : T.pickBarDown;
      move.replaceChildren(icon(atBottom ? 'arrowUp' : 'arrowDown', 20));
      move.setAttribute('aria-label', label);
      move.title = label;
    }
    renderMove();
    const root = h('div', { class: 'gsb-picker' }, [layer, hover, bar]);
    mount.append(root);

    function elementAt(x: number, y: number): Element | null {
      const hit = document.elementsFromPoint(x, y).find((el) => el !== host && el !== document.documentElement && el !== document.body);
      return hit ?? null;
    }

    function render(): void {
      frame = 0;
      if (hovered && hovered.isConnected) {
        hover.hidden = false;
        place(hover, hovered.getBoundingClientRect());
        hover.classList.toggle('gsb-pick-hover-selected', picked.includes(hovered));
      } else {
        hover.hidden = true;
      }
      while (markers.length > picked.length) markers.pop()?.remove();
      picked.forEach((el, i) => {
        let marker = markers[i];
        if (!marker) {
          marker = box('gsb-pick-marker');
          marker.append(h('span', { class: 'gsb-pick-badge' }));
          root.insertBefore(marker, bar);
          markers.push(marker);
        }
        (marker.firstChild as HTMLElement).textContent = String(i + 1);
        place(marker, el.getBoundingClientRect());
      });
      count.textContent = picked.length ? ` ${T.pickCount(picked.length)}.` : '';
    }

    function schedule(): void {
      frame ||= requestAnimationFrame(render);
    }

    function toggle(el: Element): void {
      const index = picked.indexOf(el);
      if (index >= 0) picked.splice(index, 1);
      else if (picked.length >= MAX_PICKS) announce(T.pickLimit);
      else picked.push(el);
      announce(T.pickCount(picked.length));
      schedule();
    }

    function finish(result: Element[] | null): void {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('keydown', onKey, true);
      root.remove();
      resolve(result);
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        finish(null);
      }
    }

    layer.addEventListener('pointermove', (event) => {
      hovered = elementAt(event.clientX, event.clientY);
      schedule();
    });
    layer.addEventListener('pointerleave', () => {
      hovered = null;
      schedule();
    });
    layer.addEventListener('click', (event) => {
      event.preventDefault();
      const el = elementAt(event.clientX, event.clientY);
      if (el) {
        hovered = el;
        toggle(el);
      }
    });
    move.addEventListener('click', () => {
      bar.classList.toggle('gsb-pick-bar-bottom');
      renderMove();
    });
    done.addEventListener('click', () => finish(picked));
    cancel.addEventListener('click', () => finish(null));
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('keydown', onKey, true);

    render();
    done.focus();
  });
}
